<script lang="ts" setup>
/**
 * 多步式工单页面 — 移植自 ai-undress HelpCenter.tsx
 * 流程：issue → faqAnswer / method / details → success
 * 未登录用户 → loginRequired
 * 文案全部走 i18n（helpCenter.*）
 */
import { ref, computed, onMounted } from 'vue';
import api from '~/api';
import axios from 'axios';

const { t } = useI18n();

type IssueType =
  | 'task_failed' | 'generation_slow' | 'duplicate_charge' | 'feature_locked'
  | 'cancel_subscription' | 'delete_account' | 'login_issue'
  | 'no_credits' | 'payment_failed' | 'refund' | 'other' | 'business_collaboration';

type PaymentMethod = 'telegram' | 'crypto' | 'voltpay' | 'payblis' | 'luxury' | 'other';

type Step =
  | 'issue' | 'faqAnswer' | 'method' | 'telegramDelay' | 'screenshotReminder'
  | 'details' | 'loginRequired' | 'success';

interface FaqEntry {
  value: IssueType;
  title: string;
  bullets: string[];
  action?: { href: string; label: string };
}

const FAQ_ENTRIES = computed<FaqEntry[]>(() => [
  {
    value: 'task_failed',
    title: t('helpCenter.faq.taskFailed.title'),
    bullets: [
      t('helpCenter.faq.taskFailed.b1'),
      t('helpCenter.faq.taskFailed.b2'),
      t('helpCenter.faq.taskFailed.b3'),
    ],
  },
  {
    value: 'generation_slow',
    title: t('helpCenter.faq.generationSlow.title'),
    bullets: [
      t('helpCenter.faq.generationSlow.b1'),
      t('helpCenter.faq.generationSlow.b2'),
      t('helpCenter.faq.generationSlow.b3'),
    ],
  },
  {
    value: 'duplicate_charge',
    title: t('helpCenter.faq.duplicateCharge.title'),
    bullets: [
      t('helpCenter.faq.duplicateCharge.b1'),
      t('helpCenter.faq.duplicateCharge.b2'),
      t('helpCenter.faq.duplicateCharge.b3'),
    ],
  },
  {
    value: 'feature_locked',
    title: t('helpCenter.faq.featureLocked.title'),
    bullets: [
      t('helpCenter.faq.featureLocked.b1'),
      t('helpCenter.faq.featureLocked.b2'),
    ],
    action: { href: '/pricing', label: t('helpCenter.faq.featureLocked.actionLabel') },
  },
  {
    value: 'delete_account',
    title: t('helpCenter.faq.deleteAccount.title'),
    bullets: [
      t('helpCenter.faq.deleteAccount.b1'),
      t('helpCenter.faq.deleteAccount.b2'),
    ],
    action: { href: '/my/account', label: t('helpCenter.faq.deleteAccount.actionLabel') },
  },
  {
    value: 'login_issue',
    title: t('helpCenter.faq.loginIssue.title'),
    bullets: [
      t('helpCenter.faq.loginIssue.b1'),
      t('helpCenter.faq.loginIssue.b2'),
      t('helpCenter.faq.loginIssue.b3'),
    ],
  },
]);

const FORM_ISSUES = computed<{ value: IssueType; label: string }[]>(() => [
  { value: 'no_credits', label: t('helpCenter.form.noCredits') },
  { value: 'payment_failed', label: t('helpCenter.form.paymentFailed') },
  { value: 'refund', label: t('helpCenter.form.refund') },
  { value: 'other', label: t('helpCenter.form.other') },
]);

const BUSINESS_ISSUES = computed<{ value: IssueType; label: string; hint: string }[]>(() => [
  {
    value: 'business_collaboration',
    label: t('helpCenter.business.label'),
    hint: t('helpCenter.business.hint'),
  },
]);

const PAYMENT_METHODS = computed<{ value: PaymentMethod; label: string }[]>(() => [
  { value: 'telegram', label: t('helpCenter.method.telegram') },
  { value: 'crypto', label: t('helpCenter.method.crypto') },
  { value: 'voltpay', label: t('helpCenter.method.voltpay') },
  { value: 'payblis', label: t('helpCenter.method.payblis') },
  { value: 'luxury', label: t('helpCenter.method.luxury') },
  { value: 'other', label: t('helpCenter.method.other') },
]);

const AMOUNT_OPTIONS = ['$9.99', '$29.99', '$59.99', '$249.99'];
const MIN_DESCRIPTION_LENGTH = 10;
const MAX_FILE_SIZE = 10 * 1024 * 1024;
const MAX_SCREENSHOTS = 3;

const FAQ_BY_VALUE = computed(() => new Map(FAQ_ENTRIES.value.map((e) => [e.value, e])));
const FAQ_VALUES = new Set<IssueType>(['task_failed', 'generation_slow', 'duplicate_charge', 'feature_locked', 'delete_account', 'login_issue']);
const PAYMENT_RELATED = new Set<IssueType>(['no_credits', 'payment_failed', 'refund']);

type SectionItem =
  | { kind: 'faq'; value: IssueType }
  | { kind: 'form'; value: IssueType }
  | { kind: 'business'; value: IssueType };

const SECTIONS = computed<{ title: string; emoji: string; items: SectionItem[] }[]>(() => [
  {
    title: t('helpCenter.sections.common'), emoji: '🔥',
    items: [
      { kind: 'form', value: 'no_credits' },
      { kind: 'faq', value: 'feature_locked' },
      { kind: 'faq', value: 'task_failed' },
      { kind: 'faq', value: 'generation_slow' },
      { kind: 'form', value: 'payment_failed' },
    ],
  },
  {
    title: t('helpCenter.sections.help'), emoji: '🧩',
    items: [
      { kind: 'faq', value: 'duplicate_charge' },
      { kind: 'faq', value: 'login_issue' },
      { kind: 'faq', value: 'delete_account' },
    ],
  },
  {
    title: t('helpCenter.sections.support'), emoji: '📩',
    items: [
      { kind: 'form', value: 'refund' },
      { kind: 'form', value: 'other' },
    ],
  },
  {
    title: t('helpCenter.sections.business'), emoji: '🤝',
    items: [{ kind: 'business', value: 'business_collaboration' }],
  },
]);

const authStore = useAuthStore();
const comm = useCommStore();
const router = useRouter();
const localePath = useLocalePath();

const step = ref<Step>('issue');
const issueType = ref<IssueType | null>(null);
const paymentMethod = ref<PaymentMethod | null>(null);
const email = ref('');
const amount = ref('');
const customAmount = ref('');
const description = ref('');
const screenshots = ref<string[]>([]);
const isUploading = ref(false);
const isSubmitting = ref(false);

onMounted(() => {
  if (authStore.isLogin && comm.userInfo?.email && !email.value) {
    email.value = comm.userInfo.email;
  }
});

const isDescriptionValid = computed(() => description.value.trim().length >= MIN_DESCRIPTION_LENGTH);
const needsAmount = computed(() => issueType.value ? PAYMENT_RELATED.has(issueType.value) : false);
const finalAmount = computed(() => amount.value === 'other' ? customAmount.value.trim() : amount.value);

function goToFormStep(target: Step) {
  if (!authStore.isLogin) step.value = 'loginRequired';
  else step.value = target;
}

function pickFaq(value: IssueType) {
  issueType.value = value;
  step.value = 'faqAnswer';
}

function pickForm(value: IssueType) {
  issueType.value = value;
  const target: Step = value === 'other' || value === 'refund' ? 'details' : 'method';
  goToFormStep(target);
}

function pickBusiness(value: IssueType) {
  issueType.value = value;
  goToFormStep('details');
}

function pickMethod(m: PaymentMethod) {
  paymentMethod.value = m;
  step.value = m === 'telegram' ? 'telegramDelay' : 'screenshotReminder';
}

function goBack() {
  const s = step.value;
  if (s === 'method' || s === 'faqAnswer' || s === 'loginRequired') step.value = 'issue';
  else if (s === 'telegramDelay' || s === 'screenshotReminder') step.value = 'method';
  else if (s === 'details') {
    if (issueType.value && FAQ_VALUES.has(issueType.value)) step.value = 'faqAnswer';
    else if (issueType.value === 'refund' || issueType.value === 'other' || issueType.value === 'business_collaboration')
      step.value = 'issue';
    else step.value = 'method';
  }
}

function resetAll() {
  step.value = 'issue';
  issueType.value = null;
  paymentMethod.value = null;
  amount.value = '';
  customAmount.value = '';
  description.value = '';
  screenshots.value = [];
}

async function handleFileUpload(event: Event) {
  const input = event.target as HTMLInputElement;
  const files = Array.from(input.files || []);
  if (!files.length) return;
  if (screenshots.value.length + files.length > MAX_SCREENSHOTS) {
    alert(t('helpCenter.alerts.maxScreenshots', { n: MAX_SCREENSHOTS }));
    return;
  }

  isUploading.value = true;
  try {
    for (const file of files) {
      if (file.size > MAX_FILE_SIZE) {
        alert(t('helpCenter.alerts.fileTooLarge', { name: file.name }));
        continue;
      }
      const ext = (file.name.split('.').pop() || 'png').toLowerCase();
      const sign = await api.attachUpploader(ext);
      await axios.put(sign.sign_url, file, { headers: { 'Content-Type': file.type || getMimeType(file.name) } });
      screenshots.value.push(sign.file_url);
    }
  } catch (err) {
    console.error('[HelpCenter] upload error', err);
    alert(t('helpCenter.alerts.uploadFailed'));
  } finally {
    isUploading.value = false;
    input.value = '';
  }
}

async function handleSubmit() {
  if (!email.value || !issueType.value || !isDescriptionValid.value) return;
  if (needsAmount.value && !finalAmount.value) return;

  isSubmitting.value = true;
  try {
    const lines: string[] = [];
    lines.push(`[Issue] ${issueType.value}`);
    if (paymentMethod.value) lines.push(`[Payment Method] ${paymentMethod.value}`);
    if (finalAmount.value) lines.push(`[Amount] ${finalAmount.value}`);
    lines.push('');
    lines.push(description.value.trim());
    if (screenshots.value.length > 0) {
      lines.push('');
      lines.push('[Screenshots]');
      lines.push(...screenshots.value);
    }

    await api.feedback(
      lines.join('\n'),
      screenshots.value.join(','),
      email.value,
      undefined,
      'contact',
      {
        issue_type: issueType.value || undefined,
        payment_method: paymentMethod.value || undefined,
        amount: finalAmount.value || undefined,
      },
    );
    step.value = 'success';
  } catch {
    alert(t('helpCenter.alerts.submitFailed'));
  } finally {
    isSubmitting.value = false;
  }
}

function goLogin() {
  router.push(localePath('/login/google?redirect=/contact'));
}
</script>

<template>
  <div class="hc">
    <div class="hc-header">
      <button v-if="step !== 'issue' && step !== 'success'" class="back-btn" @click="goBack" :aria-label="t('helpCenter.back')">←</button>
      <h1>{{ t('helpCenter.title') }}</h1>
    </div>

    <!-- Issue 选择 -->
    <div v-if="step === 'issue'" class="space-y-6">
      <div v-for="section in SECTIONS" :key="section.title" class="space-y-2">
        <p class="section-title">
          <span class="section-emoji">{{ section.emoji }}</span>{{ section.title }}
        </p>
        <div class="space-y-2">
          <template v-for="item in section.items" :key="`${item.kind}-${item.value}`">
            <button v-if="item.kind === 'faq'" type="button" class="row-btn" @click="pickFaq(item.value)">
              <span class="row-label">{{ FAQ_BY_VALUE.get(item.value)?.title }}</span>
              <span class="row-arrow">→</span>
            </button>
            <button v-else-if="item.kind === 'form'" type="button" class="row-btn" @click="pickForm(item.value)">
              <span class="row-label">{{ FORM_ISSUES.find((e) => e.value === item.value)?.label }}</span>
              <span class="row-arrow">→</span>
            </button>
            <button v-else type="button" class="row-btn row-business" @click="pickBusiness(item.value)">
              <div class="flex-1">
                <div class="row-label">{{ BUSINESS_ISSUES.find((e) => e.value === item.value)?.label }}</div>
                <div class="row-hint">{{ BUSINESS_ISSUES.find((e) => e.value === item.value)?.hint }}</div>
              </div>
              <span class="row-arrow">→</span>
            </button>
          </template>
        </div>
      </div>
    </div>

    <!-- FAQ Answer -->
    <div v-else-if="step === 'faqAnswer' && issueType && FAQ_VALUES.has(issueType)" class="space-y-4">
      <h2 class="answer-title">{{ FAQ_BY_VALUE.get(issueType)?.title }}</h2>
      <ul class="bullets">
        <li v-for="b in FAQ_BY_VALUE.get(issueType)?.bullets" :key="b">{{ b }}</li>
      </ul>
      <div class="space-y-2">
        <a v-if="FAQ_BY_VALUE.get(issueType)?.action"
          :href="localePath(FAQ_BY_VALUE.get(issueType)!.action!.href)"
          class="cta-link">
          {{ FAQ_BY_VALUE.get(issueType)?.action?.label }} →
        </a>
        <button class="cta-secondary" @click="resetAll">{{ t('helpCenter.gotIt') }}</button>
      </div>
      <div class="footer-link">
        <button @click="goToFormStep('details')">{{ t('helpCenter.stillNeedHelp') }}</button>
      </div>
    </div>

    <!-- 登录拦截 -->
    <div v-else-if="step === 'loginRequired'" class="space-y-4 text-center">
      <div class="login-icon">🔐</div>
      <h2 class="answer-title">{{ t('helpCenter.loginRequired.title') }}</h2>
      <p class="text-white/60">{{ t('helpCenter.loginRequired.desc') }}</p>
      <button class="cta-primary" @click="goLogin">{{ t('helpCenter.loginRequired.login') }}</button>
      <button class="cta-secondary" @click="step = 'issue'">{{ t('helpCenter.loginRequired.back') }}</button>
    </div>

    <!-- 支付方式 -->
    <div v-else-if="step === 'method'" class="space-y-3">
      <p class="text-white/60 text-sm">{{ t('helpCenter.whichMethod') }}</p>
      <div class="grid grid-cols-2 gap-2">
        <button v-for="m in PAYMENT_METHODS" :key="m.value" class="method-btn" @click="pickMethod(m.value)">
          {{ m.label }}
        </button>
      </div>
    </div>

    <!-- Telegram 延迟 -->
    <div v-else-if="step === 'telegramDelay'" class="space-y-4 text-center">
      <div class="info-icon">⏱</div>
      <h2 class="answer-title">{{ t('helpCenter.telegramDelay.title') }}</h2>
      <p class="text-white/60">
        {{ t('helpCenter.telegramDelay.desc') }}
      </p>
      <button class="cta-primary" @click="resetAll">{{ t('helpCenter.telegramDelay.wait') }}</button>
      <button class="cta-secondary" @click="step = 'details'">{{ t('helpCenter.telegramDelay.still') }}</button>
    </div>

    <!-- 截图提示 -->
    <div v-else-if="step === 'screenshotReminder'" class="space-y-4 text-center">
      <div class="info-icon">📷</div>
      <h2 class="answer-title">{{ t('helpCenter.screenshotReminder.title') }}</h2>
      <p class="text-white/60">
        {{ t('helpCenter.screenshotReminder.desc') }}
      </p>
      <button class="cta-primary" @click="step = 'details'">{{ t('helpCenter.screenshotReminder.gotIt') }}</button>
    </div>

    <!-- 填写详情 -->
    <div v-else-if="step === 'details'" class="space-y-4">
      <div class="field">
        <label>{{ t('helpCenter.details.email') }}</label>
        <!-- 占位符写死：示例邮箱含 @，vue-i18n 会把 @ 当链接消息语法报错，且该值语言中立 -->
        <input v-model="email" type="email" placeholder="your@email.com" autocomplete="email" />
      </div>

      <div v-if="needsAmount" class="field">
        <label>{{ t('helpCenter.details.amount') }} <span class="req">*</span></label>
        <select v-model="amount">
          <option value="">{{ t('helpCenter.details.amountSelect') }}</option>
          <option v-for="opt in AMOUNT_OPTIONS" :key="opt" :value="opt">{{ opt }}</option>
          <option value="other">{{ t('helpCenter.details.amountOther') }}</option>
        </select>
        <input v-if="amount === 'other'" v-model="customAmount" placeholder="$" class="mt-2" />
      </div>

      <div class="field">
        <div class="flex justify-between">
          <label>{{ t('helpCenter.details.describe') }} <span class="req">*</span></label>
          <span :class="['count', isDescriptionValid && 'valid']">
            {{ description.trim().length }}/{{ MIN_DESCRIPTION_LENGTH }}
          </span>
        </div>
        <textarea v-model="description" rows="4" :placeholder="t('helpCenter.details.describePlaceholder')" />
        <p v-if="!isDescriptionValid && description.length > 0" class="hint-error">
          {{ t('helpCenter.details.minChars', { n: MIN_DESCRIPTION_LENGTH }) }}
        </p>
      </div>

      <div v-if="authStore.isLogin" class="field">
        <label>{{ t('helpCenter.details.screenshots') }} <span class="text-white/40">{{ t('helpCenter.details.screenshotsOptional', { n: MAX_SCREENSHOTS }) }}</span></label>
        <div class="thumbs">
          <div v-for="(url, i) in screenshots" :key="url" class="thumb">
            <img :src="url" alt="" />
            <button class="thumb-x" @click="screenshots.splice(i, 1)">×</button>
          </div>
          <label v-if="screenshots.length < MAX_SCREENSHOTS" class="thumb-upload">
            <input type="file" accept="image/*" multiple class="hidden" :disabled="isUploading" @change="handleFileUpload" />
            <span v-if="isUploading">…</span>
            <span v-else>+</span>
          </label>
        </div>
      </div>

      <button class="cta-primary"
        :disabled="isSubmitting || !email || !isDescriptionValid || (needsAmount && !finalAmount)"
        @click="handleSubmit">
        {{ isSubmitting ? t('helpCenter.details.submitting') : t('helpCenter.details.submit') }}
      </button>
    </div>

    <!-- 成功 -->
    <div v-else-if="step === 'success'" class="space-y-4 text-center py-8">
      <div class="success-icon">✓</div>
      <h2 class="answer-title">{{ t('helpCenter.success.title') }}</h2>
      <p class="text-white/60">{{ t('helpCenter.success.desc') }}</p>
      <button class="cta-secondary" @click="resetAll">{{ t('helpCenter.success.another') }}</button>
    </div>
  </div>
</template>

<style scoped lang="scss">
.hc { @apply rounded-2xl bg-neutral-900/60 border border-neutral-800 p-6 lg:p-8 text-white; }
.hc-header { @apply mb-6 flex items-center gap-3; }
.hc-header h1 { @apply text-2xl lg:text-3xl font-bold; }
.back-btn { @apply size-8 flex items-center justify-center rounded-lg bg-neutral-800 hover:bg-neutral-700 text-xl; }

.section-title { @apply text-xs font-semibold uppercase tracking-wider text-white/50; }
.section-emoji { @apply mr-1.5; }

.row-btn {
  @apply flex w-full items-center gap-3 rounded-lg border border-neutral-800 bg-neutral-950/40 p-3 text-left transition;
  &:hover { @apply border-amber-500/50 bg-neutral-900; }
}
.row-business { @apply items-start; }
.row-label { @apply flex-1 text-sm font-medium; }
.row-hint { @apply mt-0.5 text-xs text-white/50; }
.row-arrow { @apply text-white/40; }

.answer-title { @apply text-lg font-semibold; }
.bullets {
  @apply space-y-2 rounded-lg bg-neutral-950/60 p-4 text-sm text-white/80;
  li { @apply flex gap-2 leading-relaxed; }
  li::before { content: '•'; @apply text-amber-400 shrink-0; }
}
.cta-link {
  @apply block w-full rounded-full text-center px-6 py-3 text-base font-semibold text-white transition-all;
  background: linear-gradient(135deg, #f97316 0%, #ea580c 50%, #c2410c 100%);
}
.cta-primary {
  @apply w-full rounded-full px-6 py-3 text-base font-semibold text-white transition-all disabled:opacity-40 disabled:cursor-not-allowed;
  background: linear-gradient(135deg, #f97316 0%, #ea580c 50%, #c2410c 100%);
}
.cta-secondary {
  @apply w-full rounded-full px-6 py-3 text-base font-semibold border border-neutral-700 hover:bg-neutral-800;
}
.footer-link {
  @apply pt-3 border-t border-neutral-800 text-center;
  button { @apply text-xs text-white/50 underline hover:text-white; }
}

.method-btn {
  @apply flex flex-col items-center gap-2 rounded-lg border border-neutral-800 bg-neutral-950/40 p-4 text-sm font-medium transition;
  &:hover { @apply border-amber-500/50 bg-neutral-900; }
}

.field { @apply space-y-2; }
.field label { @apply text-sm font-medium; }
.field input, .field select, .field textarea {
  @apply w-full rounded-md border border-neutral-700 bg-neutral-950/60 p-2.5 text-sm text-white placeholder-white/40
    focus:outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-500;
}
.field textarea { @apply resize-y; }
.req { @apply text-red-400; }
.count { @apply text-xs text-red-400; &.valid { @apply text-white/40; } }
.hint-error { @apply text-xs text-red-400; }

.thumbs { @apply flex flex-wrap gap-2; }
.thumb { @apply relative size-16 overflow-hidden rounded-lg border border-neutral-800; }
.thumb img { @apply size-full object-cover; }
.thumb-x { @apply absolute right-0.5 top-0.5 size-5 rounded-full bg-black/60 text-white text-sm leading-none; }
.thumb-upload {
  @apply flex size-16 cursor-pointer items-center justify-center rounded-lg border-2 border-dashed border-neutral-700
    text-white/50 hover:bg-neutral-900 text-2xl;
}

.login-icon, .info-icon { @apply text-5xl; }
.success-icon { @apply mx-auto size-16 rounded-full bg-emerald-500 flex items-center justify-center text-white text-3xl font-bold; }

.text-white\/60 { color: rgb(255 255 255 / 0.6); }
.text-white\/40 { color: rgb(255 255 255 / 0.4); }
.text-white\/50 { color: rgb(255 255 255 / 0.5); }
</style>
